import {
  PRODUCT_POSTHOG_EVENTS,
  type PostHogPersonProperties,
} from "encamp-shared/src/instrumentation/events";
import { getFullName } from "encamp-shared/src/utils/name";
import { useEffect, useMemo, useRef } from "react";

import { useCurrentUser } from "../hooks/useCurrentUser";
import { useTenant } from "../hooks/useTenant";
import {
  capturePosthog,
  groupPosthog,
  identifyPosthog,
  POSTHOG_LAST_SESSION_MS_KEY,
  POSTHOG_LOGGED_IN_SESSION_KEY,
  setPosthogStaffUser,
} from "./posthog";

// `logged_in` is session-scoped so it fires once per browser session, not on
// every remount (see POSTHOG_LOGGED_IN_SESSION_KEY). Session length is persisted
// across reloads via POSTHOG_LAST_SESSION_MS_KEY so the next identify can report
// "most recent session length". Both are cleared on logout by resetPosthog().

/**
 * Mounted inside the authenticated app shell. Owns two browser-only analytics
 * responsibilities:
 *   1. `identify` + tenant `group` whenever the current user/tenant resolves
 *   2. `logged_in` once per session, and session-length bookkeeping
 *
 * `$pageview` is not handled here — posthog-js captures it automatically on SPA
 * navigation via `capture_pageview: "history_change"` (see posthog.ts).
 *
 * Renders nothing. No-ops entirely when PostHog is disabled (no VITE key).
 */
export function AnalyticsIdentity() {
  const { user, isStaff, isOriginalUserStaff } = useCurrentUser();
  const { tenant, tenantId } = useTenant();
  const sessionStartRef = useRef<number>(Date.now());

  // Suppress all PostHog capture for internal users (staff, including while
  // emulating a customer). Keyed on the original user type so emulation traffic
  // on /o routes is still dropped. Runs as early as the user resolves.
  useEffect(() => {
    setPosthogStaffUser(isOriginalUserStaff);
  }, [isOriginalUserStaff]);

  const role = useMemo(() => {
    if (!user || !tenantId) {
      return undefined;
    }
    return user.UserTenant.find((ut) => ut.tenantId === tenantId)?.role?.name ?? undefined;
  }, [user, tenantId]);

  // Persist session duration when the tab is hidden or closed, so the next
  // login's identify can report the most recent session length.
  useEffect(() => {
    const recordSessionLength = () => {
      const elapsed = Date.now() - sessionStartRef.current;
      if (elapsed > 0) {
        try {
          localStorage.setItem(POSTHOG_LAST_SESSION_MS_KEY, String(elapsed));
        } catch {
          // Ignore storage failures (private mode, quota) — non-critical.
        }
      }
    };
    const onVisibility = () => {
      if (document.visibilityState === "hidden") {
        recordSessionLength();
      }
    };
    window.addEventListener("beforeunload", recordSessionLength);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.removeEventListener("beforeunload", recordSessionLength);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, []);

  // Scalar identity values pulled off `user` so the identify effect keys on the
  // values that matter rather than the `user` object reference — `useCurrentUser`
  // can return a fresh object each render, which would otherwise re-fire identify
  // on every render.
  const userId = user?.id;
  const email = user?.email;
  const name = user ? getFullName(user.person) : undefined;

  // Identify + group + logged-in once the user (and, when available, tenant) resolve.
  useEffect(() => {
    if (!userId) {
      return;
    }

    let lastSessionLengthMs: number | undefined;
    try {
      const stored = localStorage.getItem(POSTHOG_LAST_SESSION_MS_KEY);
      lastSessionLengthMs = stored ? Number(stored) : undefined;
    } catch {
      lastSessionLengthMs = undefined;
    }

    const personProperties: PostHogPersonProperties = {
      email,
      name,
      role,
      is_staff: isStaff,
      organization: tenant?.name,
      tenant_id: tenantId,
      ...(lastSessionLengthMs !== undefined && !Number.isNaN(lastSessionLengthMs)
        ? { last_session_length_ms: lastSessionLengthMs }
        : {}),
    };

    identifyPosthog(userId, personProperties);

    if (tenantId && tenant?.name) {
      groupPosthog(tenantId, { name: tenant.name });
    }

    try {
      if (!sessionStorage.getItem(POSTHOG_LOGGED_IN_SESSION_KEY)) {
        capturePosthog(PRODUCT_POSTHOG_EVENTS.LOGGED_IN, {
          ...(tenantId ? { tenant_id: tenantId } : {}),
          is_staff: isStaff,
        });
        sessionStorage.setItem(POSTHOG_LOGGED_IN_SESSION_KEY, "1");
      }
    } catch {
      // Ignore storage failures — worst case we under/over-count logged_in.
    }
  }, [userId, email, name, tenant?.name, tenantId, role, isStaff]);

  return null;
}
